import React, { FunctionComponent } from 'react';

import { graphql } from 'gatsby';
import { I18nextProvider } from 'react-i18next';

import Article from '../components/Article';
import StructuredData from '../components/Article/StructuredData';
import FluidImageProvider from '../components/FluidImageProvider';
import Footer from '../components/Footer';
import Header from '../components/Header';
import InternalLinkProvider, { InternalLink } from '../components/InternalLinkProvider';
import Seo from '../components/Seo';
import HrefLang, { LangRef } from '../components/Seo/HrefLang';
import { ArticleFragmentResponse } from '../fragments/article';
import { ArticleCardFragmentResponse } from '../fragments/article-card';
import { ArticleCategoryFragmentResponse } from '../fragments/article-category';
import { ArticleImagesFragmentResponse } from '../fragments/article-images';
import { FooterFragmentResponse } from '../fragments/footer';
import { SlugFragmentResponse } from '../fragments/slug';
import useTrackPageview from '../tracking/useTrackPageview';
import i18n from '../translations/i18n';

export type ArticlePageProps = {
  pageContext?: {
    links?: InternalLink[];
  };
  data: {
    i18nRefs: {
      nodes: LangRef[];
    };
    slug?: SlugFragmentResponse;
    sanityFeaturedArticles?: {
      title?: string;
      button_label?: string;
    };
    allSanityImageAsset?: { nodes?: ArticleImagesFragmentResponse[] };
    allSanityArticle?: { nodes?: ArticleCardFragmentResponse[] };
    sanityArticle: ArticleFragmentResponse;
    category?: ArticleCategoryFragmentResponse;
    sanityFooter?: FooterFragmentResponse;
  };
};

const ArticlePage: FunctionComponent<ArticlePageProps> = (props) => {
  const article = props.data.sanityArticle;
  const i18nRefs = props.data.i18nRefs;

  const featuredArticles = props.data.allSanityArticle?.nodes || [];
  const featuredArticle = props.data.sanityFeaturedArticles || {};

  const metadata = props.data.sanityArticle?.metadata || {};
  const images = props.data.allSanityImageAsset?.nodes || [];
  const links = props.pageContext?.links || [];

  const footer = props.data.sanityFooter;

  useTrackPageview(article?.slug?.current, 'blog');

  return (
    <I18nextProvider defaultNS={article.i18n_lang} i18n={i18n}>
      <StructuredData article={article} />
      <HrefLang refs={i18nRefs.nodes} />

      <Header universe={article?.category?.universe} />
      <InternalLinkProvider links={links}>
        <FluidImageProvider images={images}>
          <Seo i18n_lang={article.i18n_lang} {...metadata} />

          <Article
            {...article}
            featuredArticle={{
              articles: featuredArticles,
              title: featuredArticle.title,
              buttonLabel: featuredArticle.button_label,
            }}
          />
        </FluidImageProvider>
      </InternalLinkProvider>

      {footer ? <Footer {...footer} /> : null}
    </I18nextProvider>
  );
};

export default ArticlePage;

export const query = graphql`
  query ArticleQuery(
    $_id: String!
    $imageIds: [String]
    $featuredArticleIds: [String]
    $i18n_lang: String
    $i18nArticlesGlob: String!
  ) {
    i18nRefs: allSanityArticle(filter: { _id: { glob: $i18nArticlesGlob } }) {
      nodes {
        i18n_lang
        slug {
          ...SlugFragment
        }
      }
    }
    sanityFeaturedArticles {
      title
      button_label
    }
    allSanityArticle(filter: { _id: { in: $featuredArticleIds } }) {
      nodes {
        ...ArticleCardFragment
      }
    }
    allSanityImageAsset(filter: { _id: { in: $imageIds } }) {
      nodes {
        ...ArticleImagesFragment
      }
    }
    sanityArticle(_id: { eq: $_id }) {
      ...ArticleFragment
    }
    sanityFooter(i18n_lang: { eq: $i18n_lang }) {
      ...FooterFragment
    }
  }
`;
